/**
 * 庫存「入 − 出 ＝ 現時」表（P1.5-A；`decisions/015` §2.3）
 *
 * 產品負責人原話：**incoming − outgoing stock ＝ current stock in the warehouse**
 *   → 呢個元件**直接顯示公式**（唔係只顯示一個餘額），三個角色共用同一個元件。
 *
 * 紅線
 *   · 數字一律由 `GET /api/inventory/flow` 出（後端同一支 SQL；前端零計算）
 *   · ⭐ `consistent`（公式 vs `stock_balances`）唔一致 → **一定要示警**（帳本自洽）
 *   · 供應商只見到自己（後端 scope；`scoped` ＝ true 時顯示提示）
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { badge, card, errText, hint as hintStyle } from '@coeldery/ui-kit';
import { ApiError, api, type FlowResponse } from '../lib/api';

const mono: React.CSSProperties = { fontVariantNumeric: 'tabular-nums', fontWeight: 700 };
const n6 = (n: number): string => Number(n ?? 0).toFixed(6);

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '6px 8px',
  borderBottom: '1px solid var(--color-divider)',
  whiteSpace: 'nowrap',
};
const td: React.CSSProperties = {
  padding: '6px 8px',
  borderBottom: '1px solid var(--color-divider)',
  whiteSpace: 'nowrap',
};

export default function StockFlowTable() {
  const { t } = useTranslation();
  const [data, setData] = useState<FlowResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const res = await api.inventoryFlow();
        if (alive) setData(res);
      } catch (err) {
        if (alive) setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
      }
    })();
    return () => {
      alive = false;
    };
  }, [t]);

  const totals = data?.totals;

  return (
    <section style={{ ...card, padding: '16px', marginTop: '12px' }}>
      <h3 style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: 800 }}>{t('stock.formulaTitle')}</h3>
      <p style={hintStyle}>{t('stock.formulaHint')}</p>

      {error && (
        <p style={errText} role="alert">
          {error}
        </p>
      )}
      {data?.scoped && <p style={hintStyle}>{t('stock.scoped')}</p>}

      {totals && (
        <p style={{ ...hintStyle, fontSize: '15px', fontWeight: 700, color: 'var(--color-text)' }}>
          {t('stock.in')} <span style={mono}>{n6(totals.inCbm)}</span>
          {' − '}
          {t('stock.out')} <span style={mono}>{n6(totals.outCbm)}</span>
          {' ＝ '}
          {t('stock.current')} <span style={mono}>{n6(totals.currentCbm)}</span>
          {'　'}
          {totals.consistent ? (
            <span style={badge('ok')}>{t('stock.ok')}</span>
          ) : (
            <span style={badge('warn')}>{t('stock.mismatch')}</span>
          )}
        </p>
      )}

      {data && data.rows.length === 0 ? (
        <p style={hintStyle}>{t('stock.empty')}</p>
      ) : (
        <div style={{ overflowX: 'auto', marginTop: '8px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr>
                <th style={th}>{t('stock.sku')}</th>
                <th style={th}>{t('stock.inCartons')}</th>
                <th style={th}>{t('stock.inCbm')}</th>
                <th style={th}>{t('stock.outCartons')}</th>
                <th style={th}>{t('stock.outCbm')}</th>
                <th style={th}>{t('stock.currentCartons')}</th>
                <th style={th}>{t('stock.currentCbm')}</th>
                <th style={th}>{t('stock.balanceCbm')}</th>
              </tr>
            </thead>
            <tbody>
              {(data?.rows ?? []).map((r) => (
                <tr key={r.itemId}>
                  <td style={td}>
                    {r.itemName}
                    <span style={hintStyle}>（{r.barcode}）</span>
                  </td>
                  <td style={{ ...td, ...mono }}>{r.inCartons}</td>
                  <td style={{ ...td, ...mono }}>{n6(r.inCbm)}</td>
                  <td style={{ ...td, ...mono }}>{r.outCartons}</td>
                  <td style={{ ...td, ...mono }}>{n6(r.outCbm)}</td>
                  <td style={{ ...td, ...mono }}>{r.currentCartons}</td>
                  <td style={{ ...td, ...mono }}>{n6(r.currentCbm)}</td>
                  <td style={{ ...td, ...mono, color: r.consistent ? undefined : 'var(--color-accent)' }}>
                    {n6(r.balanceCbm)}
                    {!r.consistent && ' ⚠️'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
